import { useEffect, useState, useMemo } from "react";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { fetchDashboardHistory, formatApiError } from "@/lib/api";

const PERIODS = [
    { id: "week", label: "Uge" },
    { id: "month", label: "Måned" },
    { id: "year", label: "År" },
    { id: "total", label: "Total" },
];

const PERIOD_HINT = {
    week: "Sidste 7 dage",
    month: "Sidste 30 dage",
    year: "Sidste 12 måneder",
    total: "Hele perioden (ugentligt)",
};

function GrowthBadge({ pct }) {
    if (pct === null || pct === undefined) {
        return <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Minus className="w-3 h-3" />—</span>;
    }
    const positive = pct >= 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    const color = positive ? "text-emerald-600" : "text-red-500";
    const sign = positive ? "+" : "";
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
            <Icon className="w-3 h-3" />{sign}{pct}%
        </span>
    );
}

export default function HistorySection() {
    const [period, setPeriod] = useState("week");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeField, setActiveField] = useState(null); // null = show all (per spec)

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchDashboardHistory(period)
            .then((d) => { if (!cancelled) setData(d); })
            .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [period]);

    const chartData = useMemo(() => {
        if (!data) return [];
        return data.labels.map((label, i) => {
            const row = { label };
            data.fields.forEach((f) => { row[f.key] = f.data[i] ?? 0; });
            return row;
        });
    }, [data]);

    const visibleFields = useMemo(() => {
        if (!data) return [];
        if (!activeField) return data.fields;
        return data.fields.filter((f) => f.key === activeField);
    }, [data, activeField]);

    const handleFieldClick = (key) => {
        // Per spec D=A: click = solo, click same = back to all
        setActiveField((cur) => (cur === key ? null : key));
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="history-section">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                    <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Historik & vækst</h2>
                    <p className="text-xs text-slate-400 mt-0.5">{PERIOD_HINT[period]}{activeField ? ` · viser kun ${data?.fields.find((f) => f.key === activeField)?.label}` : " · viser alle felter"}</p>
                </div>
                <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="history-period-tabs">
                    {PERIODS.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`history-period-${p.id}`}
                            onClick={() => setPeriod(p.id)}
                            className={`px-4 h-8 text-sm rounded-full transition ${period === p.id ? "bg-white text-slate-900 shadow-sm font-medium" : "text-slate-500 hover:text-slate-700"}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Field toggles */}
            {data && !loading && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="history-field-toggles">
                    {data.fields.map((f) => {
                        const isActive = activeField === f.key;
                        const isDimmed = activeField && !isActive;
                        return (
                            <button
                                key={f.key}
                                data-testid={`history-toggle-${f.key}`}
                                onClick={() => handleFieldClick(f.key)}
                                className={`inline-flex items-center gap-2 px-3 h-8 rounded-full border text-xs font-medium transition ${
                                    isActive ? "border-slate-900 bg-slate-900 text-white" :
                                    isDimmed ? "border-slate-200 bg-white text-slate-400 opacity-50" :
                                    "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                }`}
                            >
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: f.color }} />
                                {f.label}
                                <span className="text-slate-400 ml-1">{f.total}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Chart */}
            <div className="h-64" data-testid="history-chart">
                {loading ? (
                    <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : error ? (
                    <div className="h-full flex items-center justify-center text-red-500 text-sm">{error}</div>
                ) : !data || chartData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-slate-400 text-sm">Ingen data for valgt periode.</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                                labelStyle={{ color: "#475569", fontWeight: 600 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="circle" />
                            {visibleFields.map((f) => (
                                <Line
                                    key={f.key}
                                    type="monotone"
                                    dataKey={f.key}
                                    name={f.label}
                                    stroke={f.color}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Totals & growth table */}
            {data && !loading && (
                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="history-totals">
                    {data.fields.map((f) => (
                        <div
                            key={f.key}
                            data-testid={`history-stat-${f.key}`}
                            onClick={() => handleFieldClick(f.key)}
                            className={`p-3 rounded-xl border cursor-pointer transition ${activeField === f.key ? "border-slate-900 bg-slate-50" : "border-slate-100 hover:border-slate-300"}`}
                        >
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: f.color }} />
                                {f.label}
                            </div>
                            <p className="text-xl font-bold text-slate-900 mt-1">{f.total}</p>
                            <GrowthBadge pct={f.growth_pct} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
